import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';

const SearchableSelect = ({ value, onChange, options, defaultLabel = "Select...", className = "", required = false, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const lowerSearch = search.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(lowerSearch));
  }, [options, search]);

  const selectedOption = options.find(o => String(o.value) === String(value));

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div 
        className={`w-full border border-slate-200 bg-white rounded-lg px-3 py-2 flex items-center justify-between min-h-[38px] text-sm ${disabled ? 'opacity-50 cursor-not-allowed bg-slate-50' : 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-primary/30'}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className={`truncate ${selectedOption ? "text-slate-800" : "text-slate-500"}`}>
          {selectedOption ? selectedOption.label : defaultLabel}
        </span>
        <ChevronDown className="w-4 h-4 text-slate-400 shrink-0 ml-2" />
      </div>
      
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 flex flex-col">
          <div className="p-2 border-b border-slate-100 flex-shrink-0">
            <input 
              autoFocus
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:border-brand-primary text-sm"
            />
          </div>
          <div className="overflow-y-auto flex-1 p-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <div
                  key={opt.value}
                  className={`px-2 py-2 rounded cursor-pointer text-sm ${String(value) === String(opt.value) ? 'bg-brand-primary/10 text-brand-primary font-medium' : 'hover:bg-slate-100 text-slate-700'}`}
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                    setSearch("");
                  }}
                >
                  {opt.label}
                </div>
              ))
            ) : (
              <div className="px-2 py-3 text-center text-slate-400 text-sm">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
